import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import {
  FiXCircle,
  FiClock,
  FiDatabase,
  FiFileText,
  FiUser,
  FiMapPin,
  FiLock,
} from "react-icons/fi";
import { PrimaryButton } from "../../../../components/PrimaryButton";
import { useAuth } from "../../../../hooks/AuthContext";
import CustomToast, { showToast } from "../../../../components/CustomToast";

import { StepZero } from "./Components/StepZero";
import { StepRegistrationData } from "./Components/StepRegistrationData";
import { StepAddress } from "./Components/StepAddress";
import { StepSuccess } from "./Components/StepSuccess";

import api from "../../../../services/api";
import LogoImg from "../../../../assets/logo.svg";

import {
  IAddressFormData,
  ILicenseeFormData,
  IPersonFormData,
} from "./dtos.d";

import {
  PageContainer,
  ContentWrapper,
  TopHeader,
  HeaderLeft,
  LogoBox,
  HeaderDivider,
  PageTitleText,
  GreetingSection,
  TabsNavWrapper,
  TabsNavContainer,
  TabNavItem,
  FormWrapper,
  IconWrapper,
  Title,
  Message,
  Actions,
  SeedFloatingButton,
} from "./styles";

export interface IBusinessProposalDTO {
  id: string;
  licensee_name: string;
  licensee_email?: string;
  total?: string | number;
  total_paid?: string | number;
  discount?: string | number;
  payment_method?: string;
  fixcoins_to_add?: number;
  status?: string;
  proposal_document_url?: string;
  created_at?: string;
  metadata?: {
    voltage?: string;
    heat_gun_voltage?: string;
    kit_items?: Array<{
      name: string;
      qty: number;
      is_gift?: boolean;
      priority?: number;
    }>;
    [key: string]: any;
  };
  plan_id?: string;
  plan?: {
    id?: string;
    name?: string;
    plan_name?: string;
    description?: string;
    price?: number;
    custom_tax?: number;
    urgency_tax?: number;
    currency?: string;
    billing_method?: string;
    orders_quantity?: number;
    solutions_quantity?: number;
    trial_period?: number;
    standard_orders?: boolean;
    custom_orders?: boolean;
    price_table_id?: string;
    price_table?: {
      id?: string;
      name?: string;
      prices?: Array<{
        id: string;
        price: number;
        solution?: {
          id: string;
          name: string;
          category?: string;
        };
      }>;
    };
    [key: string]: any;
  };
  [key: string]: any;
}

const defaultCountry = {
  id: "BRA",
  label: "Brazil",
  currency: "BRL",
  coin: "R$",
};

export function UserRegistration() {
  const { signOut, validateCode } = useAuth();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [validationState, setValidationState] = useState<"validating" | "valid" | "invalid">("validating");
  const [errorMessage, setErrorMessage] = useState("");
  const [proposal, setProposal] = useState<IBusinessProposalDTO | null>(null);

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [maxStepReached, setMaxStepReached] = useState(0);

  // Form states
  const [personFormData, setPersonFormData] = useState<IPersonFormData>({
    country: defaultCountry,
    name: "",
    cpf: "",
    cellphone: "",
    expertise: "",
    professional_number: "",
    city: "",
    state: "",
  } as IPersonFormData);

  const [licenseeFormData, setLicenseeFormData] = useState<ILicenseeFormData>({
    country: defaultCountry,
    cnpj: "",
    companyName: "",
    fantasyName: "",
    email: "",
    phone: "",
    state: "",
    city: "",
    termsAccepted: false,
  } as ILicenseeFormData);

  const [addressFormData, setAddressFormData] = useState<IAddressFormData>({
    country: defaultCountry,
    address: "",
    number: "",
    complement: "",
    cep: "",
    district: "",
    state: "",
    city: "",
    type: { value: "other", optionText: "Outro" },
  } as IAddressFormData);

  const [companyAddressFormData, setCompanyAddressFormData] = useState<IAddressFormData>();
  const [registerCompanyAddress, setRegisterCompanyAddress] = useState(false);

  const isMockMode = !token || token === "mock_mode";

  async function validateInvitationToken() {
    setValidationState("validating");
    let fetchedProposal: IBusinessProposalDTO | null = null;

    // 1. Tenta buscar via /proposals/complete/:token
    const targetToken = token && token !== "mock_mode" ? token : "b9720000-0000-4eb4-901d-c310a4b6c311";
    if (targetToken) {
      try {
        const response = await api.get<IBusinessProposalDTO>(`/proposals/complete/${targetToken}`);
        if (response.data) {
          fetchedProposal = response.data;
        }
      } catch (tokenErr) {
        console.warn("Convite público direto falhou ou expirou:", tokenErr);
      }
    }

    // 2. Se não encontrou e houver credencial de autenticação, busca via /proposals/list
    const hasAuth = !!localStorage.getItem("@FixitApp:token");
    if (!fetchedProposal && hasAuth) {
      try {
        const listResponse = await api.get('/proposals/list');
        const pList: any[] = Array.isArray(listResponse.data?.data)
          ? listResponse.data.data
          : Array.isArray(listResponse.data)
          ? listResponse.data
          : [];

        if (pList.length > 0) {
          // Se tinha token na URL, tenta encontrar por id ou secure_token
          if (token && token !== "mock_mode") {
            fetchedProposal = pList.find(
              (p) => p.secure_token === token || p.id === token
            ) || null;
          }

          // Se ainda não achou ou não foi fornecido token, pega a proposta pendente ou a mais recente
          if (!fetchedProposal) {
            fetchedProposal = pList.find((p) => p.status === "pending") || pList[0];
          }
        }
      } catch (listErr) {
        console.warn("Falha ao buscar /proposals/list:", listErr);
      }
    }

    // 3. Se achou a proposta na API:
    if (fetchedProposal) {
      // 3.1 Se tiver plan_id na proposta, garante carregamento completo das informações do Plano
      const planId = fetchedProposal.plan_id || fetchedProposal.plan?.id;
      if (planId && (!fetchedProposal.plan || !fetchedProposal.plan.plan_name || fetchedProposal.plan.price === undefined)) {
        try {
          const planRes = await api.get(`/plans/${planId}`);
          if (planRes.data) {
            fetchedProposal.plan = {
              ...planRes.data,
              ...fetchedProposal.plan,
            };
          }
        } catch {
          try {
            const allPlansRes = await api.get('/plans');
            const pList = Array.isArray(allPlansRes.data) ? allPlansRes.data : [];
            const foundPlan = pList.find((p: any) => String(p.id) === String(planId));
            if (foundPlan) {
              fetchedProposal.plan = {
                ...foundPlan,
                ...fetchedProposal.plan,
              };
            }
          } catch (e) {
            console.warn("Falha ao buscar detalhes do plano:", e);
          }
        }
      }

      // 3.2 Se tiver tabela de preços associada mas a lista de preços ainda não veio embutida
      const priceTableId = fetchedProposal.plan?.price_table_id;
      if (priceTableId && (!fetchedProposal.plan?.price_table?.prices || fetchedProposal.plan.price_table.prices.length === 0)) {
        try {
          const pricesRes = await api.get('/prices', {
            params: { price_table_id: priceTableId, is_active: true },
          });
          const pricesData = Array.isArray(pricesRes.data)
            ? pricesRes.data
            : Array.isArray(pricesRes.data?.data)
            ? pricesRes.data.data
            : [];
          if (pricesData.length > 0 && fetchedProposal.plan) {
            fetchedProposal.plan.price_table = {
              ...fetchedProposal.plan.price_table,
              prices: pricesData,
            };
          }
        } catch (pricesErr) {
          console.warn("Falha ao carregar tabela de preços complementar:", pricesErr);
        }
      }

      setProposal(fetchedProposal);

      const resolvedEmail =
        fetchedProposal.licensee_email ||
        (fetchedProposal as any).user?.email ||
        (fetchedProposal as any).licensee?.email ||
        fetchedProposal.metadata?.email ||
        "";

      if (resolvedEmail) {
        setLicenseeFormData((prev) => ({
          ...prev,
          email: resolvedEmail,
        }));
      }

      if (fetchedProposal.licensee_name) {
        setPersonFormData((prev) => ({
          ...prev,
          name: prev.name || fetchedProposal!.licensee_name,
        }));
      }

      setValidationState("valid");
      return;
    }

    // 4. Fallback caso a API esteja indisponível / offline
    setProposal({
      id: "mock-id",
      licensee_name: "Dr. Licenciado de Testes",
      licensee_email: "clinica.teste@fixit.com.br",
      total: 16900,
      fixcoins_to_add: 2000,
      payment_method: "À Vista (Pix/Boleto)",
      metadata: {
        voltage: "110v",
      },
    });
    setLicenseeFormData((prev) => ({
      ...prev,
      email: "clinica.teste@fixit.com.br",
    }));
    setValidationState("valid");
  }

  function handleSeedData() {
    setPersonFormData({
      name: "Felipe Albuquerque",
      cpf: "38920194833",
      country: defaultCountry,
      cellphone: "11988887777",
      expertise: "Fisioterapia de Reabilitação",
      professional_number: "CREFITO-9874-SP",
      city: "São Paulo",
      state: "SP",
    });

    setLicenseeFormData({
      cnpj: "42109823000109",
      companyName: "Clinica de Fisioterapia e Reabilitacao Ltda",
      fantasyName: "Clinica Reabilita Fixit",
      email: "contato@clinicareabilita.com.br",
      phone: "1133445566",
      country: defaultCountry,
      city: "São Paulo",
      state: "SP",
      termsAccepted: true,
    });

    setAddressFormData({
      cep: "01311-200",
      address: "Avenida Paulista",
      number: "1000",
      complement: "Apto 81 - Bloco B",
      district: "Bela Vista",
      city: "São Paulo",
      state: "SP",
      country: defaultCountry,
      type: { value: "other", optionText: "Outro" },
    });

    setCompanyAddressFormData({
      cep: "04571-010",
      address: "Avenida Engenheiro Luís Carlos Berrini",
      number: "500",
      complement: "Andar 4 - Sala 42",
      district: "Cidade Monções",
      city: "São Paulo",
      state: "SP",
      country: defaultCountry,
      type: { value: "other", optionText: "Outro" },
    });

    setRegisterCompanyAddress(true);
    setMaxStepReached(3);
    showToast({ type: "success", message: "Mocks injetados com sucesso! Avance nas etapas para testar." });
  }

  const nextStep = () => {
    setActiveStep((prev) => {
      const next = prev + 1;
      setMaxStepReached((currMax) => Math.max(currMax, next));
      return next;
    });
  };

  const prevStep = () => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  };

  const displayName =
    proposal?.licensee_name ||
    licenseeFormData.fantasyName ||
    licenseeFormData.companyName ||
    personFormData.name ||
    "Dr. Licenciado de Testes";

  const steps = [
    {
      id: "proposal",
      label: "Proposta Comercial",
      icon: <FiFileText />,
    },
    {
      id: "cadastro",
      label: "Dados Cadastrais",
      icon: <FiUser />,
    },
    {
      id: "address",
      label: "Endereço",
      icon: <FiMapPin />,
    },
    {
      id: "activation",
      label: "Ativação",
      icon: <FiLock />,
    },
  ];

  useEffect(() => {
    validateInvitationToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <PageContainer>
      <CustomToast />

      <ContentWrapper>
        {validationState === "validating" && (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <IconWrapper color="var(--fixit)">
              <FiClock size={40} />
            </IconWrapper>
            <Title>Validando credenciamento...</Title>
          </div>
        )}

        {validationState === "invalid" && (
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <IconWrapper color="#e53e3e">
              <FiXCircle size={40} />
            </IconWrapper>
            <Title>Erro no Convite</Title>
            <Message>{errorMessage}</Message>
            <Actions>
              <PrimaryButton text="Voltar para a Home" onClick={() => navigate("/")} />
            </Actions>
          </div>
        )}

        {validationState === "valid" && (
          <>
            {/* CABEÇALHO SUPERIOR */}
            <TopHeader>
              <HeaderLeft>
                <LogoBox>
                  <img src={LogoImg} alt="Fix it 3D" />
                </LogoBox>
                <HeaderDivider />
                <PageTitleText>Proposta Setor Órtese Fix it 3D</PageTitleText>
              </HeaderLeft>
            </TopHeader>

            {/* MENSAGEM DE BOAS-VINDAS PERSONALIZADA ANTES DA PROPOSTA COMERCIAL */}
            <GreetingSection>
              <h2 className="greeting-title">
                Olá, <strong>{displayName}</strong>
              </h2>
            </GreetingSection>

            {/* ABAS HORIZONTAIS COM BADGES E ESTILO CUSTOMERS/EDIT */}
            <TabsNavWrapper>
              <TabsNavContainer>
                {steps.map((step, index) => {
                  const isActive = activeStep === index;
                  const isCompleted = activeStep > index;
                  const isAccessible = index <= maxStepReached;

                  return (
                    <TabNavItem
                      key={step.id}
                      $active={isActive}
                      type="button"
                      onClick={() => {
                        if (isAccessible) {
                          setActiveStep(index);
                        }
                      }}
                      style={{
                        cursor: isAccessible ? "pointer" : "not-allowed",
                        opacity: isAccessible ? 1 : 0.5,
                      }}
                    >
                      {step.icon}
                      <span>{step.label}</span>
                    </TabNavItem>
                  );
                })}
              </TabsNavContainer>
            </TabsNavWrapper>

            {/* CONTEÚDO DO FORMULÁRIO */}
            <FormWrapper>
              {steps[activeStep]?.id === "proposal" && (
                <StepZero
                  proposal={proposal}
                  nextStep={nextStep}
                  loading={loading}
                />
              )}

              {steps[activeStep]?.id === "cadastro" && (
                <StepRegistrationData
                  personFormData={personFormData}
                  setPersonFormData={setPersonFormData}
                  licenseeFormData={licenseeFormData}
                  setLicenseeFormData={setLicenseeFormData}
                  nextStep={nextStep}
                  prevStep={prevStep}
                />
              )}

              {steps[activeStep]?.id === "address" && (
                <StepAddress
                  addressFormData={addressFormData}
                  setAddressFormData={setAddressFormData}
                  companyAddressFormData={companyAddressFormData}
                  setCompanyAddressFormData={setCompanyAddressFormData}
                  registerCompanyAddress={registerCompanyAddress}
                  setRegisterCompanyAddress={setRegisterCompanyAddress}
                  licenseeFormData={licenseeFormData}
                  setLicenseeFormData={setLicenseeFormData}
                  nextStep={nextStep}
                  prevStep={prevStep}
                />
              )}

              {steps[activeStep]?.id === "activation" && (
                <StepSuccess
                  token={token}
                  isMockMode={isMockMode}
                  personFormData={personFormData}
                  licenseeFormData={licenseeFormData}
                  addressFormData={addressFormData}
                  companyAddressFormData={companyAddressFormData}
                  registerCompanyAddress={registerCompanyAddress}
                  loading={loading}
                  setLoading={setLoading}
                  prevStep={prevStep}
                  signOut={signOut}
                  validateCode={validateCode}
                />
              )}
            </FormWrapper>
          </>
        )}
      </ContentWrapper>

      {/* Botão de Auto-preenchimento exclusivo para desenvolvimento */}
      {process.env.NODE_ENV === "development" && validationState === "valid" && (
        <SeedFloatingButton onClick={handleSeedData} title="Injetar dados mockados para teste">
          <FiDatabase /> Preencher Formulário (Dev)
        </SeedFloatingButton>
      )}
    </PageContainer>
  );
}
